<script setup lang="ts">
import { MAX_DEFAULT_BUDGET, MIN_DEFAULT_BUDGET } from '@/helpers/priceHelper'

const {
  mapVisible = false,
  template,
  version,
} = defineProps<{
  mapVisible?: boolean
  version: 1 | 2
  template?: 'map' | 'split' | 'list'
}>()
const emits = defineEmits<{
  'toggle-filters-modal': []
  'toggle-map': []
  'update-budget': [string, string]
  'update-rooms': [{ bathrooms: number; bedrooms: number }]
}>()

const modal = ref(false)
const modalType = ref<'rooms' | 'budget'>('rooms')

const { t } = useI18n()
const { trackMapBackButton, trackDropdownSticky, trackFiltersModal } =
  useSearchTracking()

const { maxBudget, minBudget, hasBudget } = useSearchBudgetFilters()
const { bathrooms, bedrooms } = useSearchNumericFilters()
const { budgetFormatted, roomsFormatted } = useSearchClientHeader()
const { filtersCount } = useSearchFiltersCount()

const openModal = ref(false)

const hasRooms = computed(() => bathrooms.value > 0 || bedrooms.value > 0)

const handleClick = (type: 'rooms' | 'budget') => {
  modalType.value = type
  modal.value = true

  trackDropdownSticky(type, template!)
}

const updateBudget = (max: string, min: string) => {
  modal.value = false
  emits('update-budget', max, min)
}

const updateRooms = (rooms: { bathrooms: number; bedrooms: number }) => {
  modal.value = false
  emits('update-rooms', rooms)
}

const openFiltersHandle = () => {
  trackFiltersModal(template!)
  emits('toggle-filters-modal')
}

const mapBackButtonHandle = () => {
  trackMapBackButton()
  emits('toggle-map')
}
</script>

<template>
  <div
    :class="[
      'top-0 px-4 py-2',
      version === 2 && template === 'map'
        ? 'fixed inset-x-0 z-[9999] bg-white/50'
        : 'sticky z-fixed bg-white',
    ]"
  >
    <div class="flex items-center gap-2">
      <button
        v-if="version === 2 && template === 'map'"
        class="flex flex-none"
        type="button"
        @click="mapBackButtonHandle"
      >
        <BaseIcon
          name="arrowLeft"
          aria-hidden="true"
          focusable="false"
          :size="1.2"
        />
      </button>
      <SearchInputV2MobileContainerSummary
        :class="[
          'grow',
          { 'w-full': version === 1 },
          { 'w-[calc(100%-48px)]': version === 2 },
        ]"
        @click="openModal = true"
      />
    </div>

    <div v-if="version === 1" class="mt-3 flex items-center gap-2">
      <button
        class="flex h-[35px] w-[125px] items-center border border-gray-200 px-3 text-md font-medium"
        type="button"
        @click="$emit('toggle-map')"
      >
        <BaseIcon :name="mapVisible ? 'picture' : 'map'" class="mr-2.5" />
        {{ mapVisible ? $t('search.listView') : $t('search.mapView') }}
      </button>
      <button
        class="flex h-[35px] items-center border border-gray-200 px-3 text-md font-medium"
        type="button"
        @click="$emit('toggle-filters-modal')"
      >
        {{ $t('search.filters') }} ({{ filtersCount }})
      </button>
    </div>
    <div v-else class="mt-3">
      <div class="flex flex-nowrap gap-2 overflow-x-auto scroll-smooth">
        <button
          type="button"
          class="flex flex-none items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-xs font-bold"
          @click="openFiltersHandle"
        >
          <BaseIcon
            name="filterLc"
            aria-hidden="true"
            focusable="false"
            :size="1"
          />
          {{ t('allFilters') }}
          <div
            v-if="filtersCount"
            class="flex size-[20px] items-center justify-center rounded-full bg-secondary-100 text-3xs font-bold"
          >
            {{ filtersCount }}
          </div>
        </button>
        <SearchResultHeaderV2DropdownButton
          :empty="!hasRooms"
          :open="modal && modalType === 'rooms'"
          class="flex-none"
          @clear="$emit('update-rooms', { bedrooms: 0, bathrooms: 0 })"
          @click="handleClick('rooms')"
        >
          {{ hasRooms ? roomsFormatted : t('rooms') }}
        </SearchResultHeaderV2DropdownButton>
        <SearchResultHeaderV2DropdownButton
          :empty="!hasBudget"
          :open="modal && modalType === 'budget'"
          class="flex-none"
          @clear="
            $emit('update-budget', MAX_DEFAULT_BUDGET, MIN_DEFAULT_BUDGET)
          "
          @click="handleClick('budget')"
        >
          {{ hasBudget ? budgetFormatted : t('budget') }}
        </SearchResultHeaderV2DropdownButton>
      </div>

      <BaseModalV2 v-model="modal">
        <template #header>
          <button
            class="absolute right-4 top-4 z-10"
            type="button"
            @click="modal = false"
          >
            <BaseIcon name="close" />
          </button>
        </template>
        <template #content>
          <SearchResultHeaderV2DropdownRooms
            v-if="modalType === 'rooms'"
            :bathrooms="bathrooms"
            :bedrooms="bedrooms"
            @update-rooms="updateRooms"
          />
          <SearchResultHeaderV2DropdownBudget
            v-if="modalType === 'budget'"
            :max-budget="maxBudget"
            :min-budget="minBudget"
            @update-budget="updateBudget"
          />
        </template>
      </BaseModalV2>
    </div>
  </div>

  <BaseModalV2
    anchor="top"
    class="modal-search-input-mobile"
    :model-value="openModal"
    @back="openModal = false"
    @close="openModal = false"
  >
    <template #header="{ closeModal }">
      <div class="m-4 flex items-center justify-between">
        <span class="font-monospace text-xl font-bold">
          {{ $t('search.yourSearch') }}
        </span>
        <BaseButton
          class="!text-xs"
          color="secondary"
          variant="link"
          @click="closeModal"
        >
          {{ $t('global.close') }}
        </BaseButton>
      </div>
    </template>
    <template #content>
      <SearchInputV2MobileContainer @click-search="openModal = false" />
    </template>
  </BaseModalV2>
</template>

<style scoped>
.modal-search-input-mobile {
  @apply z-[9999];
}
.modal-search-input-mobile :deep(.base-modal-wrapper) {
  @apply !overflow-visible;
}
</style>

<i18n lang="json">
{
  "en": {
    "allFilters": "All filters",
    "budget": "Budget",
    "rooms": "Rooms"
  },
  "fr": {
    "allFilters": "Tous les filtres",
    "budget": "Budget",
    "rooms": "Pièces"
  }
}
</i18n>
